import React from "react";
import { maxBy } from "lodash";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useTranslation } from "react-i18next";

import Label from "@components/form/Label";
import SelectAccountingPeriod from "@components/form/SelectAccountingPeriod";
import Loading from "@components/shared/Loading";
import Card from "@components/zenith/ui/Card";
import {
  GRID_PROPS,
  LEGEND_PROPS,
  X_AXIS_PROPS,
  Y_AXIS_PROPS,
  monthlySeries,
  netDeclarations,
} from "@components/zenith/ui/chartSeries";
import ChartDataTable from "@components/zenith/ui/ChartDataTable";
import { useAppContext } from "@context/AppContext";
import { useAllAccountingPeriods } from "@hooks/query/zenith/useAllAccountingPeriods";
import { useSubscriberDeclarations } from "@hooks/query/zenith/useSubscriberDeclarations";
import { useSubscriberLevel0Resources } from "@hooks/query/zenith/useSubscriberLevel0Resources";
import { formatVolume } from "@utils/formatVolume";
import { formatSchemePeriod } from "@utils/schemeDay";
import { usedByWaterClass } from "@utils/volumes";
import ViewBySwitch, { ViewBy } from "./ViewBySwitch";

/** Monthly declared usage over an accounting period, all zones together or
 * one series per zone or water class. Volumes are in litres. */
const ExtractionHistoryWidget: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const { currentAccountingPeriod } = useAppContext();
  const [viewBy, setViewBy] = React.useState<ViewBy>("summary");
  const [periodId, setPeriodId] = React.useState<string>();
  const periodInputId = React.useId();
  // The same query as the period picker's, so it is shared. Its periods keep
  // their ISO dates, which periodMonths needs.
  const { data: periods = [], isLoading: isLoadingPeriods } =
    useAllAccountingPeriods({
      params: { level1ResourceId: undefined },
    });
  // The chosen period, else the active one, else (between periods) the latest.
  const period =
    periods.find(
      (p: any) => p.id === (periodId ?? currentAccountingPeriod.id),
    ) ?? maxBy(periods, (p: any) => new Date(p.periodStart).getTime());
  // The period's own readings, which the API lists for any period of the scheme asked for by id.
  const { data: declarations = [], isInitialLoading } =
    useSubscriberDeclarations({
      params: { accountingPeriodId: period?.id },
      enabled: Boolean(period),
      select: (data: any) =>
        netDeclarations(data?.filter((d: any) => !d.deletedAt) ?? []),
    });
  const { data: zones = [] } = useSubscriberLevel0Resources();

  if (isInitialLoading || isLoadingPeriods) {
    return (
      <div className="flex justify-center py-20">
        <Loading />
      </div>
    );
  }

  if (!period) {
    return (
      <Card className="min-w-0" title={t("zenith.balances.extraction_history")}>
        <p className="my-16 text-center text-lg font-light text-primary-2">
          {t("common.no_data")}
        </p>
      </Card>
    );
  }

  const waterClassNames: Record<string, string> = Object.fromEntries(
    zones.flatMap((zone: any) =>
      zone.waterClasses.map((waterClass: any) => [
        waterClass.id,
        waterClass.name,
      ]),
    ),
  );
  const all = t("zenith.balances.all_level0wrs");
  const other = t("common.other");
  const { series, colorOf, dataset } = monthlySeries(
    declarations,
    period.periodStart,
    classProportions =>
      viewBy === "water_class"
        ? // A class's overuse counts with its usage, as on its card's Used.
          // Usage of a class the customer holds no entitlement in, and overuse
          // that names no class, chart as Other.
          Object.entries(usedByWaterClass(classProportions)).map(
            ([waterClass, volume]) => [
              waterClassNames[waterClass] ?? other,
              volume,
            ],
          )
        : (classProportions.proportions ?? [])
            .flatMap(Object.entries)
            .map(([zone, volume]: [string, number | string]) => [
              viewBy === "summary" ? all : zone,
              volume,
            ]),
    other,
    key => t(key),
  );

  return (
    <Card className="min-w-0" title={t("zenith.balances.extraction_history")}>
      <div className="flex flex-wrap items-end justify-between gap-2 px-5 pt-4">
        <div className="w-64 max-w-full">
          <Label htmlFor={periodInputId}>{t("common.accounting_period")}</Label>
          <SelectAccountingPeriod
            inputId={periodInputId}
            formatPeriod={formatSchemePeriod}
            value={{ id: period.id }}
            onChange={(e: any) => setPeriodId(e?.value?.id)}
          />
        </div>
        <ViewBySwitch value={viewBy} onChange={setViewBy} />
      </div>
      {series.length > 0 ? (
        <div className="h-72 px-4 pb-4 pt-5">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={dataset} margin={{ left: -8, right: 8 }}>
              <CartesianGrid {...GRID_PROPS} />
              <XAxis {...X_AXIS_PROPS} />
              <YAxis {...Y_AXIS_PROPS} />
              <Tooltip formatter={(value: number) => formatVolume(value)} />
              {viewBy !== "summary" ? <Legend {...LEGEND_PROPS} /> : null}
              {series.map((name, i) => (
                <Area
                  key={name}
                  type="monotone"
                  dataKey={name}
                  stroke={colorOf(name, i)}
                  strokeWidth={2}
                  fill={colorOf(name, i)}
                  fillOpacity={0.12}
                />
              ))}
            </AreaChart>
          </ResponsiveContainer>
          <ChartDataTable
            label={t("zenith.balances.extraction_history")}
            series={series}
            dataset={dataset}
          />
        </div>
      ) : (
        <p className="my-16 text-center text-lg font-light text-primary-2">
          {t("declaration.no_data")}
        </p>
      )}
    </Card>
  );
};

export default ExtractionHistoryWidget;
